import type { Cache } from '@urql/exchange-graphcache';
import isValueGreater from '@/utils/isValueGreater';

type OrderByValue = string | Record<string, unknown> | null;
type OrderByEntry = Record<string, OrderByValue>;
export type OrderByArg = OrderByEntry | OrderByEntry[];

type SortKey = {
  field: string;
  direction: 'asc' | 'desc';
  nulls: 'first' | 'last';
};

type CursorCache = Pick<Cache, 'resolve'>;

const isNullish = (value: unknown) => value === null || value === undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isOrderByArg = (value: unknown): value is OrderByArg =>
  Array.isArray(value)
    ? value.length > 0 && value.every(isRecord)
    : isRecord(value);

const toDirection = (value: string | undefined): 'asc' | 'desc' => {
  if (!value) {
    return 'asc';
  }

  return value.toLowerCase().includes('desc') ? 'desc' : 'asc';
};

const toNullsPosition = (
  value: string | undefined,
  direction: 'asc' | 'desc',
): 'first' | 'last' => {
  if (value?.toLowerCase().includes('nulls_first')) {
    return 'first';
  }

  if (value?.toLowerCase().includes('nulls_last')) {
    return 'last';
  }

  return direction === 'desc' ? 'first' : 'last';
};

const getSortKeys = (
  orderBy: OrderByArg,
  cursorArgument: string,
): SortKey[] => {
  const orderEntries = Array.isArray(orderBy) ? orderBy : [orderBy];
  const sortKeys: SortKey[] = [];

  orderEntries.forEach((entry) => {
    Object.entries(entry).forEach(([field, value]) => {
      if (typeof value !== 'string') {
        return;
      }

      const direction = toDirection(value);

      sortKeys.push({
        field,
        direction,
        nulls: toNullsPosition(value, direction),
      });
    });
  });

  if (!sortKeys.some((sortKey) => sortKey.field === cursorArgument)) {
    sortKeys.unshift({
      field: cursorArgument,
      direction: 'asc',
      nulls: 'last',
    });
  }

  return sortKeys;
};

const areValuesEqual = (valueOne: unknown, valueTwo: unknown) => {
  if (valueOne === valueTwo) {
    return true;
  }

  if (isNullish(valueOne) && isNullish(valueTwo)) {
    return true;
  }

  if (typeof valueOne === 'string' && typeof valueTwo === 'string') {
    const firstTimestamp = Date.parse(valueOne);
    const secondTimestamp = Date.parse(valueTwo);

    if (!Number.isNaN(firstTimestamp) && !Number.isNaN(secondTimestamp)) {
      return firstTimestamp === secondTimestamp;
    }

    return valueOne.localeCompare(valueTwo) === 0;
  }

  if (typeof valueOne === 'number' && typeof valueTwo === 'number') {
    return valueOne === valueTwo;
  }

  return false;
};

export const compareLinksByOrderBy = (
  linkOne: string,
  linkTwo: string,
  cache: CursorCache,
  orderBy: OrderByArg,
  cursorArgument: string,
) => {
  const sortKeys = getSortKeys(orderBy, cursorArgument);

  for (const sortKey of sortKeys) {
    const valueOne = cache.resolve(linkOne, sortKey.field);
    const valueTwo = cache.resolve(linkTwo, sortKey.field);

    if (areValuesEqual(valueOne, valueTwo)) {
      continue;
    }

    const valueOneIsNullish = isNullish(valueOne);
    const valueTwoIsNullish = isNullish(valueTwo);
    if (valueOneIsNullish || valueTwoIsNullish) {
      return valueOneIsNullish === (sortKey.nulls === 'first') ? -1 : 1;
    }

    const firstIsGreater = isValueGreater(valueOne, valueTwo);
    const shouldComeFirst =
      sortKey.direction === 'desc' ? firstIsGreater : !firstIsGreater;

    return shouldComeFirst ? -1 : 1;
  }

  return linkOne.localeCompare(linkTwo);
};

export default function sortByCursor(
  result: string[],
  cache: CursorCache,
  cursorArgument: string,
  orderBy: OrderByArg,
) {
  return result.sort((linkOne, linkTwo) =>
    compareLinksByOrderBy(linkOne, linkTwo, cache, orderBy, cursorArgument),
  );
}
